const trapFocusHandlers = {}

const getFocusableElements = (container) => {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  )
}

const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapFocusHandlers.focusin)
  document.removeEventListener('focusout', trapFocusHandlers.focusout)
  document.removeEventListener('keydown', trapFocusHandlers.keydown)

  if (elementToFocus) elementToFocus.focus()
}

const trapFocus = (container, elementToFocus = container) => {
  if (!container) return
  const elements = getFocusableElements(container)
  const first = elements[0]
  const last = elements[elements.length - 1]

  removeTrapFocus(container)
  if (container.hasAttribute('tabindex')) {
    container.focus()
  } else {
    first && first.focus()
  }

  trapFocusHandlers.focusin = (event) => {
    if (container !== event.target && !container.contains(event.target)) {
      first.focus();
    }

    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;
      
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  }

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = function (event) {
    if (event?.code?.toUpperCase() !== 'TAB') return // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener('focusout', trapFocusHandlers.focusout)
  document.addEventListener('focusin', trapFocusHandlers.focusin)

  if (elementToFocus) {
    elementToFocus.focus()

    if (
      elementToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length)
    }
  }
}

const focusVisiblePolyfill = () => {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN'
  ]
  let currentFocusedElement = null
  let mouseClick = null

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event?.code?.toUpperCase())) {
      mouseClick = false
    }
  })

  window.addEventListener('mousedown', (event) => {
    mouseClick = true
  })

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused')

    if (mouseClick) return
    currentFocusedElement = document.activeElement
    currentFocusedElement.classList.add('focused')
  }, true )

  window.addEventListener('blur', () => {
    if (currentFocusedElement) {
      currentFocusedElement.classList.remove('focused')
      currentFocusedElement = null
    }
  })
}

const handleFirstTab = (e) => {
  if (e.keyCode === 9) {
    document.body.classList.add('user-is-tabbing')

    window.removeEventListener('keydown', handleFirstTab)
    window.addEventListener('mousedown', handleMouseDownOnce)
  }
}

const handleMouseDownOnce = () => {
  document.body.classList.remove('user-is-tabbing')

  window.removeEventListener('mousedown', handleMouseDownOnce)
  window.addEventListener('keydown', handleFirstTab)
}

const handleDialogFocus = (element = null) => {
  if (element) {
    trapFocus(element)
  } else {
    const dialogs = document.querySelectorAll('[role="dialog"]')

    dialogs.forEach((dialog) => {
      trapFocus(dialog)
    })
  }
}

/**
 * Fix non-essential icons inside CMS-rendered content (article.content / page.content).
 * Only targets containers marked with class "js-a11y-cms-content".
 * Mirrors the static HTML fix pattern:
 *   <a aria-label="TEXT"><p aria-hidden="true">TEXT</p></a>
 */
const fixCmsContentIcons = () => {
  document.querySelectorAll('.js-a11y-cms-content').forEach((container) => {
    // 1. Hide empty icon spans (decorative, no text, no aria-label)
    container.querySelectorAll('span[class*="icon"]').forEach((span) => {
      if (!span.textContent.trim() && !span.getAttribute('aria-label')) {
        if (!span.closest('[aria-hidden="true"]')) {
          span.setAttribute('aria-hidden', 'true')
        }
      }
    })

    // 2. Fix .link-button links (type-back, size-medium, etc.) with <p> inside
    //    <a class="link-button ..." aria-label="TEXT"><p aria-hidden="true">TEXT</p></a>
    container.querySelectorAll('a.link-button').forEach((link) => {
      const p = link.querySelector(':scope > p')
      if (!p) return
      const label = p.textContent.trim()
      if (!link.getAttribute('aria-label') && label) {
        link.setAttribute('aria-label', label)
      }
      p.setAttribute('aria-hidden', 'true')
    })

    // 3. Fix a.item links with nested .text-area
    //    <a class="item" aria-label="TEXT"><div class="text-area" aria-hidden="true">...</div></a>
    container.querySelectorAll('a.item').forEach((link) => {
      const textArea = link.querySelector('.text-area')
      if (!textArea) return
      const title = textArea.querySelector('p.title, .title')
      const label = title ? title.textContent.trim() : ''
      if (!label) return
      if (!link.getAttribute('aria-label')) {
        link.setAttribute('aria-label', label)
      }
      textArea.setAttribute('aria-hidden', 'true')
    })
  })
}

/**
 * Set alt="" on CMS-rendered images.
 * Only targets containers marked with class "js-a11y-cms-images" (primarily article content).
 */
const fixCmsContentImages = () => {
  document.querySelectorAll('.js-a11y-cms-images').forEach((container) => {
    container.querySelectorAll('.c-news__image img').forEach((img) => {
      img.setAttribute('alt', '')
    })
  })
}

export {
  trapFocus,
  removeTrapFocus,
  focusVisiblePolyfill,
  handleFirstTab,
  handleMouseDownOnce,
  handleDialogFocus,
  fixCmsContentIcons,
  fixCmsContentImages
}
